/**
 * User-facing release notes — single source of truth.
 *
 * Rendered in two places:
 *  - src/components/ReleaseNotesModal.js (teacher-facing "What's New"):
 *    shows only the USER_VISIBLE_RELEASE_COUNT most recent entries.
 *  - src/pages/admin/tabs/ReleaseNotesTab.jsx (Global Admin → Logs & Audit →
 *    Release Notes): shows the full history.
 *
 * On every release: prepend the new entry here (teacher-relevant changes only —
 * see memory/project_two_whats_new.md). Do NOT add a "(Current)" suffix to the
 * version; the newest entry is labeled Current automatically. Never delete old
 * entries — the modal trims itself, and admins keep the full history.
 */

export const USER_VISIBLE_RELEASE_COUNT = 3;

export const USER_RELEASES = [
  {
    version: '3.98.1',
    date: 'November 3, 2026',
    changes: [
      'Nonsense Word Fluency → Results: a class\'s teacher can now change a score that an assistant or co-teacher gave. Before, the change was refused.',
      'On the Letter/Sound and Nonsense Word Fluency Results pages, changing a saved score no longer changes its test date.',
      'A Letter Sounds score typed over a consonants-only result can no longer be higher than the 21 letters given.',
    ]
  },
  {
    version: '3.98.0',
    date: 'November 2, 2026',
    changes: [
      'New: Reports → Student Report. Pick a class and a student to see all of their scores on one page: Letters & Sounds and Nonsense Word Fluency by quarter, every High Frequency Words and CVC Words test, and 4K Readiness, together with your notes. Press Print for a clean copy. Where the Results page shows the case a Letter Sounds test was given in (Aa or aa), the report shows it too, with a key.',
      'Your notes stay on screen and are not printed unless you tick "Include teacher notes". Then each printed note is marked "Staff copy" and the report is stamped "Staff copy — not for families". The tick clears when you pick another student. Teacher notes are still never sent home or put on the family report.',
    ]
  },
  {
    version: '3.97.0',
    date: 'November 2, 2026',
    changes: [
      'The Dashboard now shows more than Letters & Sounds. Pick an Assessment (Letters & Sounds, High Frequency Words, CVC Words or Nonsense Word Fluency), then a Test Type for Letters & Sounds. The color bands, the legend and each student\'s dot follow your pick. High Frequency Words, CVC Words and Nonsense Word Fluency use each student\'s latest test.',
      'The button beside each name now says which assessment it opens (Take L&S, Take HFW, Take CVC, Take NWF) and opens it with that student already picked. Students are listed in columns, so your classes fit higher up the page. View Full Results opens the Results page of the assessment you picked.',
      'A 📝 beside a name means the student has notes. Hover over it (or tap it on an iPad or iPhone) to read all of them, grouped by where they were written: Teacher notes, High Frequency Words, CVC Words and 4K Readiness.',
    ]
  },
  {
    version: '3.96.0',
    date: 'November 1, 2026',
    changes: [
      'New: teacher notes for each student. Click a student\'s name on the Dashboard or on the Letter/Sound Results page, then add short notes under "Teacher notes", for example what to work on next. Each note shows its date and who wrote it, and you can delete notes you wrote. For 4K students, the 4K Readiness note shows there too.',
      'Teacher notes are for school staff, not families: they are not printed or sent home. The box above them is now called "Comments for families": those comments are printed on the report that goes home.',
    ]
  },
  {
    version: '3.95.0',
    date: 'November 1, 2026',
    changes: [
      'New on the High Frequency Words and CVC Words tests: use "Add note" during or after a test to write a note about it, for example "leaves out the first sound". The note is saved with that test when you press Submit.',
      'On High Frequency Words → Results and CVC Words → Results, each test in the Detailed Word Responses table shows its note. The person who gave the test, or the class\'s teacher, can add or change it there later. Notes are for school staff, not families: they are not printed, exported or sent home.',
    ]
  },
  {
    version: '3.94.0',
    date: 'October 30, 2026',
    changes: [
      'New on High Frequency Words → Results and CVC Words → Results: click a student\'s name to see the words they still need to practice. Show it at a conference, copy the words into a message to the family, or print it. The sheet shows only that child\'s own words: no score and no other child.',
      'To print for your whole class, use "Words to practice: print for the class" under the class name: one student per page. Students with nothing to practice, or not tested yet, are skipped, and the list says who.',
    ]
  },
  {
    version: '3.93.0',
    date: 'October 29, 2026',
    changes: [
      'New on High Frequency Words → Results and CVC Words → Results: a Most-missed words box at the top of each class shows the words your students miss most, with how many students missed each one. Select a word to see who missed it.',
      'Each student is counted once, using the same results as that test\'s Small Groups page, so a word a student has since read correctly no longer counts. Words from your own word lists are counted too.',
    ]
  },
  {
    version: '3.92.1',
    date: 'October 13, 2026',
    changes: [
      'Student reports: when a student was assessed more than once on the same test in the same quarter, the student\'s report now shows the latest attempt — the same score and date as your class Results page. This update doesn\'t change or remove any saved results.',
    ]
  },
  {
    version: '3.90.0',
    date: 'October 6, 2026',
    changes: [
      'Send Feedback: when we answer a message you sent, the reply now shows up in the app — as a card at the top of your home page the next time you sign in. Press Got it when you have read it.',
      'Help → Send Feedback has a new tab, Your messages: everything you have sent us, with our reply under each one.',
      'Also new this week: Small Groups for CVC Words and for High Frequency Words. Open CVC Words → Small Groups or High Frequency Words → Small Groups and click "Just show me the groups" to see your students in small groups based on the words they missed, with words to practice for each group.',
    ]
  },
  {
    version: '3.89.1',
    date: 'October 6, 2026',
    changes: [
      'High Frequency Words: the built-in word lists are now called Units (Unit 1 to Unit 5) instead of Groups — on the assessment, practice, results and settings pages. The words and their order are the same; only the name changed.',
      'On High Frequency Words pages, "group" now only means a small group of students (Small Groups).',
      'Also new this week: CVC words now follow each class\'s grade by default — capital letters for a 4K class, lowercase for Kindergarten and up. If you have ever saved Assessment Settings, your CVC words keep the case they have now until you change it in Management → Assessment Settings → CVC Words → Letter case.',
    ]
  },
  {
    version: '3.89.0',
    date: 'October 6, 2026',
    changes: [
      'High Frequency Words: the test page now says how the words are shown — as written, in lowercase letters or in capital letters — with a link to change it.',
      'The setting is in Management → Assessment Settings → High Frequency Words → Letter case. Nothing changes unless you change it.',
    ]
  },
  {
    version: '3.88.0',
    date: 'October 6, 2026',
    changes: [
      'New: Small Groups for CVC Words. Open CVC Words → Small Groups and click "Just show me the groups" to see your students grouped by short vowel, based on the words they missed, with words to practice and a teaching tip for each group.',
      'New: Small Groups for High Frequency Words. Open High Frequency Words → Small Groups to see your students grouped by the words they missed in common, with up to five focus words for each group. A student whose missed words nobody else missed, or whose matching group is full, is listed on a One-to-one card.',
      'On both pages, students who have not been tested yet are listed separately from students who are on track. Groups can be printed and are worked out fresh from your results each time.',
    ]
  },
  {
    version: '3.87.0',
    date: 'October 6, 2026',
    changes: [
      'CVC Words: the test page now says whether the words are shown in capital or lowercase letters, with a link to change it.',
      'New default for CVC words — Automatic by grade: capital letters for a 4K class, lowercase for Kindergarten and up.',
      'If you have ever saved Assessment Settings, your CVC words keep the case they have now (capital letters unless you chose lowercase). To change it, go to Management → Assessment Settings → CVC Words → Letter case.',
    ]
  },
  {
    version: '3.86.4',
    date: 'October 6, 2026',
    changes: [
      'Letter/Sound assessments: lowercase letter cards use the font you pick under Assessment Font again. Since the September 30 update they showed a different letter style whatever the list said; they are back to "Teachers" unless you choose another font.',
      'The newer letter style is still there if you prefer it: on the Letter/Sound assessment page, pick "Letter Card Sans" at the bottom of the Assessment Font list.',
      'Letters stay the same size from card to card, whichever font you pick.',
    ]
  },
  {
    version: '3.86.3',
    date: 'September 30, 2026',
    changes: [
      'Class Report: the printout now always fits the page. Columns are no longer cut off at the right edge, and the column headings print on every page.',
      'If your report shows more than 12 assessment columns, the rest continue on the next sheet with the student names repeated.',
    ]
  },
  {
    version: '3.86.2',
    date: 'September 30, 2026',
    changes: [
      'Sounds – Long Vowels and Sounds – Short Vowels: the letters are now the same size, and sit in the same place on the card, as on the other lowercase Sounds tests.',
      'The vowel tests now always show lowercase letters. They no longer follow the case of the Sounds test you picked before them.',
    ]
  },
  {
    version: '3.86.0',
    date: 'September 30, 2026',
    changes: [
      'Sounds can now be given in lowercase or consonants only — pick the row under Test Type. Results stay in the same Sounds column; consonants-only scores show out of 21.',
      'Under Test Type, the vowel tests are now named "Sounds – Long Vowels" and "Sounds – Short Vowels". Their cards now show the letter in the same case (uppercase or lowercase) as the Sounds test you last picked.',
    ]
  },
  {
    version: '3.85.4',
    date: 'September 30, 2026',
    changes: [
      'Class Report: 4K Readiness now shows Name Writing (Yes / No) as a column. It is also the last column of your exports; turn it off with the Columns button if you do not want it.',
    ]
  },
  {
    version: '3.85.3',
    date: 'September 30, 2026',
    changes: [
      'Class Report: saving your Columns layout now shows that it is working until the save is finished.',
    ]
  },
  {
    version: '3.85.2',
    date: 'September 30, 2026',
    changes: [
      'Class Report: a column you turn off with the Columns button is now hidden on the page and on the printed report too, not only in your exports. If you saved a layout before today, open Columns, check what is ticked, then press Save to apply it to the page.',
    ]
  },
  {
    version: '3.85.1',
    date: 'September 30, 2026',
    changes: [
      'Class Report: an assessment you hid in Assessment Settings no longer appears in the report, its print or its exports.',
      'Class Report: the Print button now shows "Opening print…" right away, so you know your click was received.',
    ]
  },
  {
    version: '3.85.0',
    date: 'September 29, 2026',
    changes: [
      'Letter cards are now the same size throughout a run.',
    ]
  },
  {
    version: '3.84.11',
    date: 'September 29, 2026',
    changes: [
      'The Phonemic Awareness Diagnostic is temporarily unavailable. Your other assessments are not affected.',
    ]
  },
  {
    version: '3.84.10',
    date: 'September 26, 2026',
    changes: [
      "Take Assessment now opens the Test Type you've selected on the dashboard (Uppercase, Lowercase, Sounds, Short or Long Vowels), and the dashboard keeps that selection as you move between students.",
    ]
  },
  {
    version: '3.84.8',
    date: 'September 26, 2026',
    changes: [
      'Fixed: a rare sign-in error could show the setup screen to existing teachers.',
    ]
  },
  {
    version: '3.84.6',
    date: 'September 25, 2026',
    changes: [
      '4K Readiness: every student without a note now has an Add note button.',
    ]
  },
  {
    version: '3.84.5',
    date: 'September 25, 2026',
    changes: [
      '4K Readiness: add a note after the assessment, and click the note icon to edit one.',
    ]
  },
  {
    version: '3.84.4',
    date: 'September 25, 2026',
    changes: [
      'New on the Class Report: Save to Google Drive — your export lands in your Google Drive as a Google Sheet, in the columns you chose.',
    ]
  },
  {
    version: '3.84.0',
    date: 'September 24, 2026',
    changes: [
      'New on the Class Report: choose and order the columns you export, copy the table straight into your spreadsheet, or download it as a file.',
    ]
  },
  {
    version: '3.83.0',
    date: 'September 24, 2026',
    changes: [
      'Class Report: pick students and classes from a list when filtering, and any assessment you haven\'t used yet is called out above the table.',
    ]
  },
  {
    version: '3.82.1',
    date: 'September 24, 2026',
    changes: [
      'New: Reports → Class Report shows your whole class across every assessment on one page — sort or filter any column, choose Fall / Winter / Spring, switch between mastery levels and scores, print it, or download it to share.',
    ]
  },
  {
    version: '3.76.2',
    date: 'September 14, 2026',
    changes: [
      'New: a 60-second tour of the app — open Help at the bottom of the left menu and choose Take the tour.',
    ]
  },
  {
    version: '3.76.0',
    date: 'September 14, 2026',
    changes: [
      'New: short how-to videos for every assessment — open Help at the bottom of the left menu and choose How-to videos.',
    ]
  },
  {
    version: '3.75.0',
    date: 'September 14, 2026',
    changes: [
      'Long Vowels and Short Vowels cards now show both the capital and the lowercase letter.',
    ]
  },
  {
    version: '3.74.0',
    date: 'September 10, 2026',
    changes: [
      'New: the Phonemic Awareness Diagnostic is now available — an auditory-only, teacher-administered diagnostic with four grade tiers (4K, Kindergarten, Grade 1, Grades 2–3), keyboard scoring, and item-level results by quarter. Find it in the left menu.',
    ]
  },
  {
    version: '3.73.1',
    date: 'September 10, 2026',
    changes: [
      'On Results, a red letter now shows what the student said instead.',
      'The Detailed Vowel Responses table on Results has its own Student, Test and Quarter filters.',
    ]
  },
  {
    version: '3.73.0',
    date: 'September 10, 2026',
    changes: [
      'Letter/Sound now includes two optional checks: Long Vowels (the sound that says its name) and Short Vowels. Use them if your grade tests vowels separately; skip them if not. Each has its own column on Results.',
    ]
  },
  {
    version: '3.71.0',
    date: 'September 1, 2026',
    changes: [
      'Cleaner class names: classes synced from your district now show a shorter name — for example "4K AM - Yilmaz" instead of "4K AM - Yilmaz - 1,2,3,Activity".',
      'Rename your class: prefer your own label? Open Classes & Students and click Rename on the class. Leave the name empty to go back to the automatic one.',
    ]
  },
  {
    version: '3.69.3',
    date: 'August 31, 2026',
    changes: [
      'Faster, more reliable sign-in on Safari and iPhone — fixed a browser issue that could make the app hang while loading right after you sign in.',
    ]
  },
  {
    version: '3.69.2',
    date: 'August 31, 2026',
    changes: [
      'Signing back in after the inactivity sign-out is more reliable. If loading ever takes too long after you sign in, you\'ll now see a clear "Try again" screen instead of the sign-in page sitting there doing nothing.',
    ]
  },
  {
    version: '3.68.0',
    date: 'August 22, 2026',
    changes: [
      'New: the Nonsense Word Fluency screener is now available — assess decoding with nonsense words and track class results by quarter. Find it in the left menu.',
    ]
  },
  {
    version: '3.66.0',
    date: 'August 22, 2026',
    changes: [
      'Phonemic Awareness is assessed in its own Phonemic Awareness section. It has been removed from the Letter/Sound results table.',
    ]
  },
  {
    version: '3.64.0',
    date: 'August 21, 2026',
    changes: [
      'If your classes and students sync automatically from Clever, the Classes & Students page now shows them as managed by your district: roster editing tools are replaced with a note explaining that changes (a new student, a name fix, a class move) are made by your school office in the student information system and flow into the app automatically. You can still download roster CSVs, manage co-teachers/assistants, and write report-card comments as before. Teachers who manage their own rosters see no change, and if you have a mix, only the district-synced classes are read-only.',
    ]
  },
  {
    version: '3.60.0',
    date: 'August 20, 2026',
    changes: [
      'Clever district sign-in is here: if your school uses Clever, you can now use the "Log in with Clever" button on the sign-in page at literacyassessments.com (or start from your Clever Portal) instead of a Google sign-in. Your classes, students, and past assessments are exactly where you left them — Clever sign-in connects to your existing account.',
    ]
  },
  {
    version: '3.58.0',
    date: 'August 19, 2026',
    changes: [
      'The app now supports 2nd and 3rd Grade classrooms: both grades are available in your teaching profile\u2019s grade list, and 2nd/3rd-grade students and classes are fully supported across assessments and reports.',
    ]
  },
  {
    version: '3.56.0',
    date: 'August 12, 2026',
    changes: [
      'For student privacy on shared devices, the app now signs you out after 30 minutes without any activity. Two minutes before that you\'ll see a "Still there?" message with a countdown and a Stay signed in button, so you\'re never signed out mid-lesson without warning. If you are signed out, you\'ll see a friendly note on the sign-in page — nothing is lost, and an assessment you just submitted always finishes saving first.',
    ]
  },
  {
    version: '3.55.0',
    date: 'August 12, 2026',
    changes: [
      'New optional 4K Readiness: one tap runs quick flashcard checks for shapes, numbers, and colors, plus quick pencil-grip, name-writing, and dexterity observations and a private notes box — and you can stop and pick up where you left off any time. Find it at the top of the left menu — or hide it in Assessment Settings if your class doesn\'t use it.',
    ]
  },
  {
    version: '3.45.0',
    date: 'August 6, 2026',
    changes: [
      'The Update Teaching Profile window now always shows your saved grade level and role — even if they aren\'t in your district\'s current dropdown list — so reopening and saving your profile never clears them.',
    ]
  },
  {
    version: '3.44.0',
    date: 'August 5, 2026',
    changes: [
      'Our support email has changed: you can now reach us at support@literacyassessments.com. The old @4kliteracyassessment.com address is being retired, and every "contact us" link in the app now points to the new one.',
    ]
  },
  {
    version: '3.38.1',
    date: 'July 23, 2026',
    changes: [
      'If your class roster is synced from your school or district, editing a student\'s name or class now shows a heads-up: changes made here get replaced at the next roster sync, so you\'ll want to update the student at the source instead. Teachers who manage their own rosters just get a quick confirmation before saving. (This also makes moving a student between your classes work reliably, with their results coming along.)',
    ]
  },
  {
    version: '3.38.0',
    date: 'July 23, 2026',
    changes: [
      'You can now fix a student\'s name or move them to the right class from Classes & Students — and their assessment results come with them. Open a student\'s Edit button to correct a typo in their name, or to move a student who was added to the wrong class. Renaming updates their name everywhere instantly. Moving a student to another class brings all of their saved results along, so nothing is lost — you\'ll see a quick confirmation telling you how many results are moving.',
    ]
  },
  {
    version: '3.37.3',
    date: 'July 23, 2026',
    changes: [
      'Lowercase letter skills now show lowercase letters everywhere. When you\'re looking at a lowercase skill (like Lowercase Letter Names), the Results tables and every Small Groups view now display the letters in lowercase — "a b d" instead of "A B D" — so it\'s always clear which skill you\'re working on. Uppercase and Sounds skills are unchanged, and your scores are exactly the same as before.',
    ]
  },
  {
    version: '3.37.2',
    date: 'July 23, 2026',
    changes: [
      'Faster page loads across the app. We trimmed the amount of code your device downloads before the app can start by about a third — so the Dashboard, the assessment pages and the login screen all appear sooner, especially on a phone or a slower connection.',
      'The growth chart and a few less-used screens now load only when you open them, instead of being downloaded up front with everything else.',
    ]
  },
  {
    version: '3.37.0',
    date: 'July 22, 2026',
    changes: [
      'The Letter Recognition Assessment page now loads noticeably faster, especially on a phone. It was making five separate trips to the server before showing you anything — including fetching your school calendar twice — and now makes three.',
      'Faster loading on the Dashboard, Results, Small Groups, and the HFW, CVC and Screener pages too: the app now asks for your students and their results at the same time instead of one after the other.',
      'Pages open faster in general. The app no longer downloads parts you are not using (like the admin screens) before showing you your own, which cuts about a fifth off the initial download.',
      'If a page ever gets stuck loading, it now stops after 15 seconds and shows a clear message with a Retry button instead of sitting on “Loading data…” indefinitely. This now works on every page — several screens, including the assessment pages, were missing it.',
    ]
  },
  {
    version: '3.36.2',
    date: 'July 22, 2026',
    changes: [
      'Fixed a sign-in problem on the pilot (dev) site where the Google sign-in window went to a broken address instead of Google. If you were affected, signing in works normally again — no changes needed on your end.',
    ]
  },
  {
    version: '3.36.0',
    date: 'July 21, 2026',
    changes: [
      'The app now works properly on iPhones and iPads! Tables scroll smoothly instead of getting cut off, buttons and dropdowns are comfortably tap-sized, and the screen no longer zooms in unexpectedly when you tap a field. If you assess students one-on-one with your phone, everything you need is now reachable on screens as small as an iPhone SE.',
      'Nothing changed on laptops and desktops — your existing setup looks and works exactly the same.',
    ]
  },
  {
    version: '3.34.3',
    date: 'July 14, 2026',
    changes: [
      'If a student was assessed more than once for the same test and quarter, the Detailed Letter Responses table now shows only the most recent result — so the Incorrect Count row counts each student once. A "Show all attempts" checkbox above the table brings back the older attempts whenever you want to compare.',
    ]
  },
  {
    version: '3.34.2',
    date: 'July 14, 2026',
    changes: [
      'No more "Unknown" students in the Detailed Letter Responses table. Results from students who were removed from your roster are now hidden by default, so the Incorrect Count row reflects your current class. A checkbox above the table lets you show them again — labeled with the student\'s name when it\'s still available, or "Removed student" otherwise.',
    ]
  },
  {
    version: '3.34.1',
    date: 'July 14, 2026',
    changes: [
      'The Letter/Sound Results page now shows only letter and sound assessments. Screener results (First Sound Fluency and Nonsense Word Fluency) no longer appear as extra, mostly-empty rows in the Detailed Letter Responses table.',
    ]
  },
  {
    version: '3.33.0',
    date: 'July 14, 2026',
    changes: [
      'Clear messages when something goes wrong: if a page can\'t load your data, you\'ll now see a short explanation and a Retry button instead of a stuck "Loading" screen or an unexpectedly empty page.',
      'Faster pages: the Dashboard, Results, Small Groups, and printed student reports now fetch only the current school year\'s scores, so they load noticeably faster as your class history grows.',
      'The Verify button on the Dashboard now works when reviewing assessments submitted by your classroom assistant.',
      'Adding a student to a brand-new class from Student Management works again, and large roster imports save in one safe step.',
      'New-account setup: the onboarding form now saves reliably (it previously could fail with a permissions error).',
    ]
  },
  {
    version: '3.32.1',
    date: 'July 14, 2026',
    changes: [
      'More reliable teacher comments: the popup now always loads your latest saved comments (even if you edited them in another tab), and earlier dated comments can no longer be accidentally overwritten.',
      'Print polish: the report table keeps a steady full-page size when printing. Tip: if a printout ever looks small, check that your browser zoom is 100% and the print dialog’s "Scale" (under More settings) is set to Default.',
    ]
  },
  {
    version: '3.32.0',
    date: 'July 14, 2026',
    changes: [
      'Teacher comments now carry a date: the printed report shows when the comment was written, right above your text.',
      'Your earlier comments are kept too — click "🕘 View previous comments" in the student popup to look back at what you wrote before. Editing on the same day simply updates that day’s comment; writing on a new day starts a fresh dated entry.',
    ]
  },
  {
    version: '3.31.0',
    date: 'July 14, 2026',
    changes: [
      'The "Saved Report Cards" panel at the bottom of the Results page has been tucked away for now to keep the page focused — your report card drafts are untouched and the panel will return in a future update.',
    ]
  },
  {
    version: '3.30.1',
    date: 'July 14, 2026',
    changes: [
      'Fixed two printing glitches with the student report: printing from the Dashboard no longer includes your class lists below the report, and adding teacher comments no longer shrinks the report — it prints full size with your comments at the bottom.',
    ]
  },
  {
    version: '3.30.0',
    date: 'July 14, 2026',
    changes: [
      'Printing a student report is now one step: click \u{1F5A8}️ Print Report in the student popup and the print dialog opens right there — no separate page. The printout still has the professional header with the student’s name, academic year, and your name.',
      'Want to add a note for the parent? Click "+ Add teacher comments" in the popup — your comments print on the report and are saved automatically, so they’ll be there next time you open that student.',
      'Fixed: the printable report showed empty results even for students you had already assessed. Scores now appear correctly.',
    ]
  },
  {
    version: '3.29.1',
    date: 'July 13, 2026',
    changes: [
      'Fixed: the \u{1F5A8}️ Print Report button showed "Student not found or access denied" for students added this school year. Printable reports now open correctly for every student.',
    ]
  },
  {
    version: '3.29.0',
    date: 'July 13, 2026',
    changes: [
      'Letter and Letter Sounds assessments: typing the letter the student said now counts as CORRECT when it matches the letter on the screen \u2014 you\u2019re simply typing what you heard, and a match means they got it right. Typing any other letter still marks it incorrect and records their answer.',
      'The app now follows your district\u2019s Academic Calendar to know when the school year starts and ends \u2014 not a fixed date. Before your school year begins, pages will show a friendly note with the start date instead of empty screens.',
      'Starting fresh? Assessment and results pages now guide you to add your class and students first instead of showing blank dropdowns.',
    ]
  },
  {
    version: '3.28.1',
    date: 'July 13, 2026',
    changes: [
      'A fresh start for the new school year: the app now shows only the current school year (July 1 \u2013 June 30). Last year\u2019s classes, students, and results have been archived \u2014 they\u2019re safely stored, just out of your way. Every page starts clean for 2026-2027.',
      'To set up your new year, add students or import your class roster on the Classes & Students page \u2014 everything you add is automatically part of the current school year.',
      'Hovering over a student\u2019s detailed letter results (in the student popup and on the Results page) now shows what the student actually said on missed letters, along with how long each letter took.',
    ]
  },
  {
    version: '3.28.0',
    date: 'July 12, 2026',
    changes: [
      'The Results page briefly gained a School Year picker. It was replaced the next day (see 3.28.1): the app now simply always shows the current school year, with previous years archived.',
    ]
  },
  {
    version: '3.27.0',
    date: 'July 12, 2026',
    changes: [
      'Letter and Letter Sounds assessments can now record what a student actually said when they miss a letter. Instead of pressing ← for an incorrect answer, press the letter key the student said — it\'s marked incorrect and their answer is saved. The ← key still works exactly as before if you just want to mark it incorrect.',
      'On the Results page, hover over any square in the Detailed Letter Responses table to see more: incorrect answers show what the student said plus how long they took; correct answers show how long they took.',
    ]
  },
  {
    version: '3.26.0',
    date: 'July 12, 2026',
    changes: [
      'What\'s New now shows just the three most recent updates, so it\'s quicker to see what actually changed since you last looked. Older notes aren\'t gone — the full history is kept for your administrators.',
    ]
  },
  {
    version: '3.25.2',
    date: 'July 9, 2026',
    changes: [
      'Practice mode is now guaranteed to never save student results — practice has always been for warm-up only, and we\'ve locked that promise in at the database level. Nothing you do on a Practice page is recorded for any student.',
      'Removed an old, unused "Practice Results" page. Practice sessions were never recorded, so the page was always empty. Your Practice pages themselves are unchanged.',
    ]
  },
  {
    version: '3.19.0',
    date: 'May 26, 2026',
    changes: [
      'Coming soon: a new helper that can draft a first version of a student\'s report-card narrative from their assessment scores — which you\'ll always review and edit before sharing with families. It\'s not switched on yet; we\'ll let you know the moment it\'s ready.',
    ]
  },
  {
    version: '3.18.2',
    date: 'May 25, 2026',
    changes: [
      'Fixed a problem where tapping "Submit Assessment" didn\'t save the assessment — saving works again for every assessment type. And if a save ever does fail, you\'ll now see a clear message instead of nothing happening.',
    ]
  },
  {
    version: '3.18.0',
    date: 'May 25, 2026',
    changes: [
      'The Correct, Incorrect, and Back buttons now look and sit like the arrow keys on a keyboard — Back on top, Incorrect and Correct below — to match the keyboard shortcuts. Each button shows just the arrow; hover over one to see what it does. Practice pages use the same arrow keys for Previous and Next.',
    ]
  },
  {
    version: '3.17.1',
    date: 'May 25, 2026',
    changes: [
      'You can now customize the High Frequency Words assessment under Assessment Settings — pick the default word group, the order words appear in, capital or lowercase letters, or enter your own list of sight words. For a custom list you can choose to keep your words in the exact order you typed them, or have them shuffled each time.',
    ]
  },
  {
    version: '3.16.1',
    date: 'May 25, 2026',
    changes: [
      'Fixed an issue where some teachers opened the app and saw no classes, students, or scores. Your classes are back — no action needed on your part.',
    ]
  },
  {
    version: '3.16.0',
    date: 'May 14, 2026',
    changes: [
      'Strengthened data protections behind the scenes, including improved Firestore security rules and tighter role-based access.',
      'Cleaned up the Class Management page to focus on the essentials: manual student entry and CSV roster import/export.',
      'Improved keyboard shortcut hints on assessment pages for faster letter and sound entry.',
      'General performance and reliability improvements.',
    ]
  },
  {
    version: '3.12.0',
    date: 'May 13, 2026',
    changes: [
      'Class Management and Results exports now include links to safe demo CSV templates and a template guide.',
      'Templates use fake sample students only and cover generic roster import and assessment results planning.',
      'The guide reminds users not to include sensitive fields and to use only files their school or district has authorized.',
    ]
  },
  {
    version: '3.11.0',
    date: 'May 13, 2026',
    changes: [
      'Results pages now have a safe CSV export option for assessment summaries.',
      'You can preview the export before downloading, and the export follows the class and table filters already selected on the page.',
      'Default assessment exports do not include item-by-item responses, timing details, PINs, parent contact fields, or other sensitive student information.',
    ]
  },
  {
    version: '3.10.0',
    date: 'May 13, 2026',
    changes: [
      'Class Management now has a safer roster CSV import/export flow.',
      'Roster imports and exports show clear school/district authorization language, keep sensitive fields out of default exports, and include a preview before downloading roster CSVs.',
    ]
  },
  {
    version: '3.9.4',
    date: 'May 13, 2026',
    changes: [
      'A small first-time tip now appears near assessment buttons: Keyboard shortcut: → Correct, ← Incorrect, ↑ Back.',
      'Teachers can dismiss the tip, and the app remembers that choice in the browser without saving anything to student records or Firestore.',
    ]
  },
  {
    version: '3.9.3',
    date: 'May 13, 2026',
    changes: [
      'Assessment buttons now show the keyboard shortcuts right on the screen: Back [↑], Incorrect [←], and Correct [→].',
      'A small tip under the buttons reminds teachers they can use the arrow keys for faster assessment.',
      'This applies to Letter/Sound, Benchmark Screener, HFW, and CVC assessments. The existing keyboard shortcuts and scoring behavior are unchanged.',
    ]
  },
  {
    version: '3.9.2',
    date: 'May 13, 2026',
    changes: [
      'Security update: roster and access control improvements have been tightened behind the scenes. PINs are now handled by the server and stored only as secure hashes.',
      'Roster imports continue to reject PIN, parent-contact, and other sensitive columns, and roster exports stay limited to basic class roster fields.',
    ]
  },
  {
    version: '3.9.1',
    date: 'May 8, 2026',
    changes: [
      'School View: the quarter dropdown and drilldown header now read simply Q1, Q2, Q3, Q4 instead of "Quarter 2025Q1" — easier to scan at a glance.',
      'School View: deleted students no longer appear as "(unknown student)" rows in the "who missed letter X" drilldown, and their old assessments no longer inflate the miss counts. If a student has been removed from your class, they\'re excluded from the school-wide picture.',
    ]
  },
  {
    version: '3.9.0',
    date: 'May 8, 2026',
    changes: [
      'For administrators: a new Roles & Labels tab lets you customize the wording your district uses — for example, calling the TLS role "Reading Specialist" or adding a grade level your defaults don\'t include. Each district can override any label without affecting other districts.',
      'For administrators: the Schools tab now has a Manage Users button on every school. It shows everyone currently assigned to that school and everyone in the district who isn\'t — assigning or removing a user is one click.',
      'For teachers: nothing visibly changes day-to-day. The dropdowns you fill out (Grade Level, Your Role) now match whatever your district admin has configured.',
    ]
  },
  {
    version: '3.8.0',
    date: 'May 8, 2026',
    changes: [
      'For TLS / Reading Specialist users: a new "Recommended Groups" page suggests cross-class focus groups automatically — same algorithm the teacher Small Groups page uses, but pulling kids from every class in your school. Click Save on the ones that fit and the involved teachers see them in their Small Groups page.',
      'When you click a letter on Find Students Who Need Help, each student row now shows their other gaps too (e.g. "Also missed: A K U Lowercase") and a link to view their full report — so you have context before deciding whether to pull them.',
      'New "All quarters (latest results)" option at the top of the quarter picker. Lets you see who\'s *currently* still missing each letter across the whole year, instead of being forced to pick one quarter.',
    ]
  },
  {
    version: '3.7.0',
    date: 'May 8, 2026',
    changes: [
      'When you update your profile, your district and school are now dropdowns instead of typed-in text. This stops typos like "Swanson" vs "Swanson Elementary" from quietly fragmenting the data your TLS or coach uses to spot students who need help.',
      'If you don\'t see your school in the list, click "My school isn\'t listed" and type it in — an administrator will review and add it to the registry, and you can keep using the app right away.',
      'No action needed if you\'ve never edited your profile — your existing school name still works.',
    ]
  },
  {
    version: '3.6.1',
    date: 'May 7, 2026',
    changes: [
      'Behind the scenes polish on the new School View — the quarter picker now only shows quarters where students have actually been assessed, instead of listing future quarters that haven\'t happened yet.',
    ]
  },
  {
    version: '3.6.0',
    date: 'May 7, 2026',
    changes: [
      'New "School View" for Teaching Learning Specialists — coming soon to your school. If you\'re a TLS, your district admin will turn this on for your account; teachers don\'t need to do anything to set it up.',
      'For teachers: when your TLS pulls a few of your students into a focus group (for example, "students who missed the letter M"), that group will now show up at the top of your Small Groups page next time you open it. You\'ll see which of your students are in it, what skill they\'re practicing, and any note your TLS added (like when they plan to pull the kids).',
      'Nothing changes about how you do assessments, look at results, or use Small Groups for your own class. The pull-out group section just appears above your class\'s groups when there\'s one to show.',
    ]
  },
  {
    version: '3.5.12',
    date: 'May 7, 2026',
    changes: [
      'The empty row that appeared above the headers on the class summary table is gone — the table now starts right at the top of the bordered area.',
    ]
  },
  {
    version: '3.5.11',
    date: 'May 7, 2026',
    changes: [
      'Polished the v3.5.10 boxes on the class summary table: the Phonemic group is now fully boxed on the right side, the extra black line under the Student column is gone, and the box border weight is slightly thinner so the frame looks more refined.',
    ]
  },
  {
    version: '3.5.10',
    date: 'May 7, 2026',
    changes: [
      'The class summary table on the Letter / Sound Results page now has a real box around each test type — Uppercase, Lowercase, Sounds, and Phonemic — so each group is visually framed. Horizontal lines between students are also clearly visible now, making it easier to track a row across the page.',
    ]
  },
  {
    version: '3.5.9',
    date: 'May 7, 2026',
    changes: [
      'The Letter / Sound Results page tables now have the same clearly visible grid lines as the per-student report — both the class summary table and the Detailed Responses table. Easier to track rows across with your finger or eye.',
    ]
  },
  {
    version: '3.5.8',
    date: 'May 7, 2026',
    changes: [
      'Print Report page now has a "← Back to Student List" button so you can get back to your students without using the browser back arrow.',
      'Manually entered scores now show on the printed report. Before, if you typed a backdated score (like 18) on the Results page instead of running the assessment live, the Total row on the printout showed 0. The score now appears correctly.',
      'The Letter / Sound results table now has clearly visible grid lines between letters and between quarters, inside each Uppercase / Lowercase / Sounds box. The thick black borders around the boxes stay the same — just the inner lines are now actually visible on paper.',
    ]
  },
  {
    version: '3.5.7',
    date: 'May 7, 2026',
    changes: [
      'Printed reports now keep their colors. The colored cells, the chart, and the highlighted boxes all print the way they look on screen — you no longer need to find the "Background graphics" checkbox in the print dialog.',
      'The website footer at the bottom of every page (Privacy Policy / Terms of Service / © Yilmaz Education LLC) no longer appears on printed sheets. Cleaner reports for parents.',
      'One thing the app can\'t change: some browsers also add a tiny URL, date, or page number along the very edge of the printed page. That\'s a browser setting (look for "Headers and footers" in the print dialog\'s "More settings"). Untick it once and your browser will remember it.',
    ]
  },
  {
    version: '3.5.6',
    date: 'May 7, 2026',
    changes: [
      'Printing a student report is simpler. The student details popup used to have two buttons — "Quick Print" and "Professional Report" — that mostly did the same thing. Now there is just one: 🖨️ Print Report. It opens the proper printable report page (with the student name, academic year, and your name on top) and you click Print from there.',
      'On the report page, the Teacher Comments box is now hidden behind a "+ Add teacher comments" link. If you don\'t need to add anything, you can print right away — same speed as the old Quick Print. If you do want to add comments (or you already saved some on this student), the box opens up to type or edit them, and they\'ll show in the printout.',
    ]
  },
  {
    version: '3.5.5',
    date: 'May 6, 2026',
    changes: [
      'CVC Results — the column headers now show just Q1 / Q2 / Q3 / Q4 instead of "2026 Q1 / 2026 Q2 / …". The year was visually noisy and sometimes confusing. If you want to know exactly when a score was recorded, hover over the cell — the full date is in the tooltip.',
    ]
  },
  {
    version: '3.5.4',
    date: 'May 6, 2026',
    changes: [
      'CVC Results — added a Detailed Word Responses table under each class summary, just like the HFW Results page has. Filter by Student, Grade Level, or Quarter. Pick a specific grade and you\'ll see a full word grid with green/red squares per response plus a yellow "Incorrect Count" summary row showing which words tripped up the most students. Pick "All" grades and you\'ll see only the words each student missed, listed in red.',
    ]
  },
  {
    version: '3.5.3',
    date: 'May 6, 2026',
    changes: [
      'HFW and CVC Assessment pages now have the same vertical Word Size slider that the Letter/Sound assessment has — drag it to make the flashcards bigger or smaller. Your size preference is shared across all three flashcard assessments, so set it once and it sticks.',
    ]
  },
  {
    version: '3.5.2',
    date: 'May 6, 2026',
    changes: [
      'CVC Words — you can now choose whether words appear in CAPITAL letters (default) or lowercase. Find it under Assessment Settings → CVC Words → Letter case. Capital is best for early decoders; switch to lowercase once students are comfortable reading lowercase text in books.',
      'CVC Practice — letter case can also be flipped on the Practice page itself, just for that session, without changing your saved setting.',
      'CVC Results — when you have multiple classes, the page now shows one table per class (with a clear class header) instead of one big combined table. Same layout as the Letter/Sound and HFW Results pages.',
    ]
  },
  {
    version: '3.5.1',
    date: 'May 6, 2026',
    changes: [
      'CVC Words — new "Mixed (all grades)" option in Assessment Settings → CVC Words. 25 words pulled from across all four grade levels, balanced across the five short vowels. Useful for multi-age classrooms or whenever you want variety without typing your own list.',
      'Sidebar tweaks now happen instantly. When you change "Visible Assessment Types" and click Save, the menu groups slide closed (or open) right away — no need to refresh the page.',
      'CVC Assessment — fixed an issue where the Submit button looked like it didn\'t do anything. If saving fails for any reason, you now see a clear error message instead of a silent click.',
    ]
  },
  {
    version: '3.5.0',
    date: 'May 6, 2026',
    changes: [
      'NEW — CVC Words! Find it in the sidebar between High Frequency Words and Dolch. Three pages: an Assessment to track decoding, a Results page to see how each student is doing per quarter, and a Practice mode for whenever you want to flip through the words.',
      'Pick the right CVC list for your students under Assessment Settings → CVC Words. Choose Pre-K (about 10 words, short A and short I), Kindergarten (about 25 words, all five short vowels), Grade 1 (about 30 words), Grade 2–3 (Intervention) for older students still mastering CVC, or Custom if you want to type your own list.',
      'Sidebar tidy-up — under Assessment Settings → Visible Assessment Types you can now hide the menu groups you don\'t use (HFW or CVC). Hiding a group doesn\'t delete any data; it just removes the menu items so your sidebar shows only what you teach. Turn things back on any time.',
      'Assessment Settings has a new tabbed layout — three big tabs at the top (Letter Presentation Order, CVC Words, Visible Assessment Types) so you can jump straight to the section you want.',
      'The page formerly called "Class Management" is now "Classes & Students," and "Assessment Setup" is now "Assessment Settings" — clearer names, same pages.',
    ]
  },
  {
    version: '3.4.0',
    date: 'May 6, 2026',
    changes: [
      'New on Assessment Setup — you can now use a different letter order for the Uppercase, Lowercase, and Sounds tests. Pick "Custom Letter Sequence", uncheck "Use the same order for all three tests", and three editors appear (one per test). Each has its own Smart Randomize button and validation. If you don\'t change anything, your existing order keeps working as before.',
    ]
  },
  {
    version: '3.3.9',
    date: 'May 5, 2026',
    changes: [
      'Small Groups — softer wording on the page. The advanced filters and the explainer no longer use the word "struggle"; they now talk about which letters students are missing.',
    ]
  },
  {
    version: '3.3.8',
    date: 'May 5, 2026',
    changes: [
      'Internal reliability improvements. No changes to teacher or classroom features in this release.',
    ]
  },
  {
    version: '3.3.7',
    date: 'May 5, 2026',
    changes: [
      'Small Groups — fixed a bug where re-assessing a student in the same quarter was silently ignored. The page was using whichever attempt happened to be saved first instead of your most recent one, so any improvements on retakes weren\'t reflected. Now the most recent attempt always wins.',
      'Small Groups — each group card now shows the individual letters each student missed under their name (with the group\'s "focus letters" bolded so you can see which gaps are shared and which are personal to that student). Added a short note clarifying that focus letters are the *shared* gaps — students may still have additional letters they personally need to work on.',
    ]
  },
  {
    version: '3.3.6',
    date: 'May 4, 2026',
    changes: [
      'Small Groups page — new "ⓘ How are these groups created?" link explains, in plain language, exactly how the app picks the groups and the focus letters. Click it to read a four-step walkthrough.',
      'User Guide — added a Small Groups section so the same explanation lives under Help if you want to read it without leaving your current page.',
    ]
  },
  {
    version: '3.3.5',
    date: 'May 4, 2026',
    changes: [
      'Internal build/reliability fix. No changes to teacher or classroom features in this release.',
    ]
  },
  {
    version: '3.3.4',
    date: 'May 4, 2026',
    changes: [
      'Sidebar groups (Letter/Sound, High Frequency Words, Management, Help, etc.) now start collapsed each time the app loads. Click a group header to expand it. Keeps the menu compact on first open.',
    ]
  },
  {
    version: '3.3.3',
    date: 'May 4, 2026',
    changes: [
      'Sidebar is more compact — menu items are tighter together so the full list usually fits on screen without needing to scroll. Touch targets are still comfortably tappable.',
      '"What\'s New" and "Send Feedback" moved into a new "Help" section in the sidebar (alongside the User Guide), instead of sitting as separate buttons at the bottom.',
    ]
  },
  {
    version: '3.3.2',
    date: 'April 30, 2026',
    changes: [
      'Letter Recognition Assessment: after sliding the "Letter Size" bar on the side of the page, the keyboard arrow keys now work right away — no extra click on the page needed before pressing ← / → / ↑ to mark Correct, Incorrect, or go Back.',
    ]
  },
  {
    version: '3.3.1',
    date: 'April 29, 2026',
    changes: [
      'Sidebar now clearly shows when there are more menu options below — a soft fade at the bottom edge, a thin always-visible scrollbar, and a pulsing "▾ More below" tap-target appear automatically whenever items run past the visible area. Click "More below" to jump down through the list.',
    ]
  },
  {
    version: '3.3.0',
    date: 'April 29, 2026',
    changes: [
      'New — Display Settings: go to Management → Display Settings to adjust the text size and menu font used across the app. Especially helpful on large 4K or ultrawide monitors where the default menus can feel small. Five sizes (Small to Largest) and five font choices. Your preference is saved on this device.',
      'The kid-facing assessment letters and flashcard words still use the original child-friendly font no matter which menu font you pick.',
    ]
  },
  {
    version: '3.2.0',
    date: 'April 28, 2026',
    changes: [
      'New — Small Groups: go to Letter/Sound → Small Groups to instantly see suggested teaching groups based on your class\'s assessment data. Each group highlights shared letter weaknesses and includes a plain-English teaching tip.',
      'Sidebar improvement: collapsing the navigation now shows a slim icon bar on desktop — all sections stay clickable without reopening. Mobile behavior unchanged.',
      'Letter/Sound and High Frequency Words tabs now have distinct colors for faster visual scanning.',
      'Assess button is now right next to each student\'s name in both the Letter/Sound and HFW Results tables for one-tap access.',
    ]
  },
  {
    version: '3.1.0',
    date: 'April 22, 2026',
    changes: [
      'Early Literacy App is now operated by Yilmaz Education LLC, a Wisconsin limited liability company.',
      'Internal code quality improvements and documentation updates.',
      'Various reliability improvements to assessment and reporting flows.',
    ]
  },
  {
    version: '3.0.0',
    date: 'April 15, 2026',
    changes: [
      'Platform expanded for all U.S. states. Grade labels updated: "4K" is now "Pre-K" and "5K" is now "Kindergarten" throughout the app.',
      'No changes to classroom features, assessments, or student data.',
    ]
  },
  {
    version: '2.16.0',
    date: 'April 15, 2026',
    changes: [
      'Teachers from TPT or other channels can now log in with email and password — no school Google account needed. Admins can generate a one-click account-setup link from the Licenses tab for any individual email license.',
    ]
  },
  {
    version: '2.15.0',
    date: 'April 14, 2026',
    changes: [
      'Platform improvements: academic calendar management, activity monitoring, and infrastructure updates. No changes to teacher or classroom features in this release.',
    ]
  },
  {
    version: '2.14.3',
    date: 'April 13, 2026',
    changes: [
      'Platform improvements: admin tools and infrastructure. No changes to teacher or classroom features.',
    ]
  },
  {
    version: '2.13.0',
    date: 'April 13, 2026',
    changes: [
      'Onboarding: your district is now selected from a verified dropdown instead of a free-text field. If your district isn\'t listed, you can type it in and an administrator will review it.',
    ]
  },
  {
    version: '2.12.0',
    date: 'April 12, 2026',
    changes: [
      'Added new Subscription Tiers for licenses: Paid, Beta, and Complimentary.',
      'Implemented Beta Access Activity Tracking to prevent license hoarding (flags for >30 & >90 days inactive).',
      'Secure soft-deletion and Insta-Expire actions added to the Licenses dashboard.'
    ]
  },
  {
    version: '2.11.0',
    date: 'April 9, 2026',
    changes: [
      'Added a new "Both letters" test type in Practice Mode to display upper and lowercase letters together.',
      'Established dedicated deployment pipelines for isolated preview and development testing.',
      'Restricted demo functionalities strictly to authorized preview domains.'
    ]
  },
  {
    version: '2.10.0',
    date: 'April 6, 2026',
    changes: [
      'Added a new Letter Size adjustment slider to flashcards in Practice and Assessment modes that automatically saves to your profile.',
      'Eliminated the UI "flicker" during impersonation session swaps for seamless transitions.',
      'Replaced browser alerts with clean React Modal components for User Role assignments (e.g., Teacher, Admin).',
      'Added class-aware safety warnings when attempting to strip an educational assistant\'s access.'
    ]
  },
  {
    version: '2.9.3',
    date: 'April 3, 2026',
    changes: [
      'Completely overhauled text rendering engine: letters now use an algebraic centering formula that completely eliminates invisible boundary bleeding.',
      'Perfected flashcard accessibility geometry: letters scale optimally and strictly bound their ink within the pastel borders across all fonts.',
      'Synchronized the Practice Mode layout to use the exact same responsive container dimensions as the Assessment Mode.'
    ]
  },
  {
    version: '2.9.2',
    date: 'March 25, 2026',
    changes: [
      'Fixed dropdown font size appearing smaller than expected — select elements now explicitly inherit the page font size across all browsers.',
      'Fixed impersonation failure: the admin session restore token no longer errors when a reason is required.',
    ]
  },
  {
    version: '2.9.1',
    date: 'March 25, 2026',
    changes: [
      'Student PINs are now stored as secure bcrypt hashes — plaintext PINs are no longer written to the database.',
      'Added admin Maintenance tab with a one-click PIN hash migration tool for existing records.',
      'Impersonation now requires an auditable reason before proceeding, logged permanently.',
      'Confirmed licensing enforcement is active — all users are checked against the license registry on login.',
    ]
  },
  {
    version: '2.9.0',
    date: 'March 25, 2026',
    changes: [
      'Strengthened access controls: teachers and EAs can now only view students in their own classes.',
      'Added rate limiting to all admin Cloud Functions to prevent abuse.',
      'Improved CSV upload security: file size limit (5 MB), MIME type check, and field sanitization.',
      'Moved the admin restore token out of browser storage into secure memory to reduce XSS exposure.',
      'Enforced Content Security Policy headers across all pages.',
      'Fixed cryptographically insecure PIN generation in all remaining upload paths.',
    ]
  },
  {
    version: '2.8.1',
    date: 'March 25, 2026',
    changes: [
      'Applied additional backend security hardening including stronger Firestore write rules and cryptographically secure PIN generation.',
      'Added HTTP security headers to improve browser-level protection for all users.',
    ]
  },
  {
    version: '2.8.0',
    date: 'March 25, 2026',
    changes: [
      'Hardened Firebase security by migrating all API keys to environment variables.',
      'Neutralized past security risks by scrubbing git history and decommissioning redundant keys.',
      'Fixed login redirect to ensure users land directly on the Dashboard after authentication.',
      'Added intelligent routing that redirects authenticated users away from the login page.',
    ]
  },
  {
    version: '2.7.4',
    date: 'March 2026',
    changes: [
      'Added "Send Feedback" capability so you can report bugs or suggest features directly from the app.',
      'Added "Delete Class" button to hide incorrectly created classes without data loss.',
      'Completed migration of all student lists to strictly Last Name Alphabetical sorting for consistency.',
    ]
  },
  {
    version: '2.7.3',
    date: 'March 2026',
    changes: [
      'Added visual indicators for Unlicensed Users in the Admin Dashboard.',
      'Moved "Teacher Group" (4K vs Kindergarten) assignment to the User Management tab to better reflect per-teacher licensing.',
      'Stabilized Multi-Factor Authentication (MFA) flows.'
    ]
  },
  {
    version: '2.7.2',
    date: 'Early March 2026',
    changes: [
      'Added capability to sort columns in the Admin Dashboard.',
      'Improved security tracking and audit logs.',
      'Enabled explicit impersonation for support administration.'
    ]
  }
];
